import {
  changeCompanyV1,
  changeDraftCustomerV1,
  changeInvalidFieldsV1,
  cleanDirtyDraftV1,
  previewDraftV1,
} from 'client/actions/DocumentsV1/draftV1/draftV1Actions'
import { IThunkAction } from 'client/actions/index'
import { setNavigationPath } from 'client/actions/navigation'
import { ongoingAdd, ongoingRemove } from 'client/actions/ongoing'
import * as api from 'client/api/draft'
import { DeliverynoteDraftInterface } from 'client/components/DocumentsV1/Deliverynote/DeliverynoteInterface'
import { ReceiptDraftInterface } from 'client/components/DocumentsV1/Receipt/ReceiptInterface'
import { CustomerInterface } from 'client/Interfaces/CustomerInterface'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { EditedDraftV1Interface } from 'client/Interfaces/Draft/v1/EditedDraftV1Interface'
import { isStringValid } from 'client/Other/functions/stringFunctions'
import { Immutable } from 'client/Other/Immutable'
import { customersSelector } from 'client/reducers/customer'
import { getDraftV1Selector } from 'client/reducers/draftV1Reducer'
import { tradeNameV1Selector } from 'client/reducers/settings'

type DraftInterface = ReceiptDraftInterface | DeliverynoteDraftInterface

const cleanCustomer = (payload: CustomerInterface): CustomerInterface => {
  const cleanPayload: CustomerInterface = Immutable.copy(payload)

  if (payload && payload.id) {
    cleanPayload.customerId = payload.id
    delete cleanPayload.id
  }
  if (cleanPayload.name) {
    delete cleanPayload.name
  }
  if (cleanPayload.displayName) {
    delete cleanPayload.displayName
  }
  if (cleanPayload.eInvoiceAddress) {
    delete cleanPayload.eInvoiceAddress
  }
  return cleanPayload
}

const documentSpecificInvalidFields = (documentType, draft: EditedDraftV1Interface) => {
  let documentSpecificInvalidFields: Array<keyof EditedDraftV1Interface> = []
  switch (documentType) {
    case DocumentTypes.DELIVERYNOTE:
      if (!draft.deliverynoteNumber) {
        documentSpecificInvalidFields = [...documentSpecificInvalidFields, 'deliverynoteNumber']
      }
      return documentSpecificInvalidFields
    case DocumentTypes.RECEIPT:
      if (!draft.receiptNumber) {
        documentSpecificInvalidFields = [...documentSpecificInvalidFields, 'receiptNumber']
      }
      return documentSpecificInvalidFields
    default:
      return []
  }
}

export const validateDraftV1Thunk = (documentType: DocumentTypes): IThunkAction<string, string[]> => {
  return (dispatch, getState) => {
    const draft = getDraftV1Selector(getState())
    let invalidFields: Array<keyof EditedDraftV1Interface> = documentSpecificInvalidFields(documentType, draft)

    if (draft.invoiceNumber && !isStringValid(draft.invoiceNumber)) {
      invalidFields = [...invalidFields, 'invoiceNumber']
    }

    if (!draft.customer || !draft.customer.customerId) {
      invalidFields = [...invalidFields, 'customer']
    }

    if (!isStringValid(draft.description)) {
      invalidFields = [...invalidFields, 'description']
    }

    if (!isStringValid(draft.footerText)) {
      invalidFields = [...invalidFields, 'footerText']
    }
    dispatch(changeInvalidFieldsV1(invalidFields))
  }
}

export const changeDraftCustomerV1Thunk = (customer: CustomerInterface): IThunkAction<null, CustomerInterface> => {
  return (dispatch) => {
    if (customer && customer.language) {
      dispatch(changeDraftCustomerV1(cleanCustomer(customer)))
    } else {
      dispatch(changeDraftCustomerV1(null))
    }
  }
}

export const setNewCustomerV1Thunk = (customer: CustomerInterface): IThunkAction<null, CustomerInterface> => {
  return (dispatch, getState) => {
    const chosenCustomer = customersSelector(getState()).collection.find((c) => c?.id === customer?.id)
    if (chosenCustomer) {
      dispatch(changeDraftCustomerV1Thunk(cleanCustomer(chosenCustomer)))
    } else {
      dispatch(changeDraftCustomerV1(null))
    }
  }
}

export const fetchPreviewForDraftV1 = (
  documentType: DocumentTypes,
): IThunkAction<null, string[] | string, Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    dispatch(validateDraftV1Thunk(documentType))
    const draft = { ...getDraftV1Selector(getState()) }
    if (draft?.company?.tradeName.logo?.content) {
      draft.company.tradeName.logo.content = null
    }
    if (!draft.invalidFields) {
      const cleanedDraft = getDraftV1Selector(getState()) as DraftInterface
      dispatch(ongoingAdd('PREVIEW_DRAFT'))
      if (documentType === DocumentTypes.DELIVERYNOTE) {
        dispatch(setNavigationPath('/deliverynote-preview'))
      } else if (documentType === DocumentTypes.RECEIPT) {
        dispatch(setNavigationPath('/receipt-preview'))
      }
      const preview = await api.getHtmlPreviewV1(documentType, cleanedDraft)
      dispatch(previewDraftV1(preview))
      dispatch(ongoingRemove('PREVIEW_DRAFT'))
    }
  }
}

export const reSyncCompanyAfterUpdateThunk = (): IThunkAction<null, EditedDraftV1Interface['company']> => {
  return (dispatch, getState) => {
    const draft = getDraftV1Selector(getState())
    const company = tradeNameV1Selector(getState(), draft?.company?.tradeName._id)
    dispatch(changeCompanyV1(company))
  }
}
